/**
 * @fileoverview Matchmaking Test Runner
 *
 * Executes automated tests against Firebase services for matchmaking,
 * account management, ratings, and game flows.
 */

import { MatchmakingTest, MatchmakingTestResult, MatchmakingTestSuite } from "./types";

/**
 * Run a single matchmaking test
 */
export async function runMatchmakingTest(test: MatchmakingTest): Promise<MatchmakingTestResult> {
  const startTime = performance.now();

  try {
    const result = await test.run();
    return {
      ...result,
      duration: result.duration || performance.now() - startTime,
    };
  } catch (error) {
    return {
      passed: false,
      message: `Test threw an error: ${error instanceof Error ? error.message : String(error)}`,
      details: {
        error: error instanceof Error ? error.stack : String(error),
      },
      duration: performance.now() - startTime,
    };
  }
}

/**
 * Run all tests in a matchmaking test suite
 */
export async function runMatchmakingTestSuite(
  suite: MatchmakingTestSuite
): Promise<MatchmakingTestSuiteResult> {
  const results: Map<string, MatchmakingTestResult> = new Map();
  const startTime = performance.now();

  for (const test of suite.tests) {
    const result = await runMatchmakingTest(test);
    results.set(test.name, result);
  }

  const passed = Array.from(results.values()).filter((r) => r.passed).length;
  const failed = Array.from(results.values()).filter((r) => !r.passed).length;

  return {
    suite,
    results,
    passed,
    failed,
    total: suite.tests.length,
    duration: performance.now() - startTime,
  };
}

export interface MatchmakingTestSuiteResult {
  suite: MatchmakingTestSuite;
  results: Map<string, MatchmakingTestResult>;
  passed: number;
  failed: number;
  total: number;
  duration: number;
}

/**
 * Helper to create a passing test result
 */
export function passTest(message: string, details?: Record<string, unknown>): MatchmakingTestResult {
  return {
    passed: true,
    message,
    details,
    duration: 0,
  };
}

/**
 * Helper to create a failing test result
 */
export function failTest(
  message: string,
  expected?: unknown,
  actual?: unknown
): MatchmakingTestResult {
  return {
    passed: false,
    message,
    details: { expected, actual },
    duration: 0,
  };
}

/**
 * Assert helper for matchmaking tests
 */
export function assert(
  condition: boolean,
  message: string,
  expected?: unknown,
  actual?: unknown
): void {
  if (!condition) {
    throw new AssertionError(message, expected, actual);
  }
}

export class AssertionError extends Error {
  expected?: unknown;
  actual?: unknown;

  constructor(message: string, expected?: unknown, actual?: unknown) {
    super(message);
    this.name = "AssertionError";
    this.expected = expected;
    this.actual = actual;
  }
}

/**
 * Assert that two values are equal
 */
export function assertEqual<T>(actual: T, expected: T, message?: string): void {
  if (actual !== expected) {
    throw new AssertionError(
      message || `Expected ${expected} but got ${actual}`,
      expected,
      actual
    );
  }
}

/**
 * Assert that a value is within a range
 */
export function assertInRange(
  value: number,
  min: number,
  max: number,
  message?: string
): void {
  if (value < min || value > max) {
    throw new AssertionError(
      message || `Expected ${value} to be between ${min} and ${max}`,
      `${min} - ${max}`,
      value
    );
  }
}

/**
 * Assert that a value is truthy
 */
export function assertTrue(value: unknown, message?: string): void {
  if (!value) {
    throw new AssertionError(message || `Expected truthy value but got ${value}`, true, value);
  }
}

/**
 * Assert that a value is defined (not null/undefined)
 */
export function assertDefined<T>(value: T | null | undefined, message?: string): asserts value is T {
  if (value === null || value === undefined) {
    throw new AssertionError(
      message || `Expected defined value but got ${value}`,
      "defined",
      value
    );
  }
}
